/** Dashboard widget identifiers for the alpha dashboard. */
export type DashboardWidgetId =
  | 'digibot'
  | 'trading-window'
  | 'quick-actions'
  | 'my-tasks'
  | 'company-news'
  | 'meetings';

export interface DashboardWidgetConfig {
  id: DashboardWidgetId;
  /** Display order (lower = higher on page). */
  order: number;
  /** Bootstrap 12-column span (4, 6, 8, or 12). */
  colSpan: 4 | 6 | 8 | 12;
  visible: boolean;
  collapsed: boolean;
}

export interface DashboardUserLayoutPayload {
  dashboardKey: string;
  widgets: DashboardWidgetConfig[];
}

export interface DashboardWidgetMeta {
  id: DashboardWidgetId;
  title: string;
  description?: string;
  /** Allowed column spans in edit mode. */
  allowedColSpans: Array<4 | 6 | 8 | 12>;
}

export const DASHBOARD_ALPHA_KEY = 'alpha';

export const DASHBOARD_WIDGET_REGISTRY: DashboardWidgetMeta[] = [
  {
    id: 'digibot',
    title: 'DigiBot',
    allowedColSpans: [12],
  },
  {
    id: 'trading-window',
    title: 'Trading Window',
    allowedColSpans: [12],
  },
  {
    id: 'quick-actions',
    title: 'Quick Actions',
    allowedColSpans: [12],
  },
  {
    id: 'my-tasks',
    title: 'My Task',
    allowedColSpans: [6, 8, 12],
  },
  {
    id: 'company-news',
    title: 'Company News',
    allowedColSpans: [4, 6, 12],
  },
  {
    id: 'meetings',
    title: 'Meetings',
    allowedColSpans: [6, 8, 12],
  },
];

export const DEFAULT_DASHBOARD_LAYOUT: DashboardWidgetConfig[] = [
  { id: 'digibot', order: 0, colSpan: 12, visible: true, collapsed: false },
  { id: 'trading-window', order: 1, colSpan: 12, visible: true, collapsed: false },
  { id: 'quick-actions', order: 2, colSpan: 12, visible: true, collapsed: false },
  { id: 'my-tasks', order: 3, colSpan: 8, visible: true, collapsed: false },
  { id: 'company-news', order: 4, colSpan: 4, visible: true, collapsed: false },
  { id: 'meetings', order: 5, colSpan: 8, visible: true, collapsed: false },
];

export function mergeWithDefaultLayout(
  saved: DashboardWidgetConfig[] | null | undefined
): DashboardWidgetConfig[] {
  const defaults = DEFAULT_DASHBOARD_LAYOUT.map((w) => ({ ...w }));
  if (!saved || !saved.length) {
    return defaults;
  }
  const byId = new Map(saved.map((w) => [w.id, w]));
  return defaults.map((def) => {
    const fromSaved = byId.get(def.id);
    if (!fromSaved) {
      return def;
    }
    const meta = DASHBOARD_WIDGET_REGISTRY.find((m) => m.id === def.id);
    const allowed = meta?.allowedColSpans ?? [def.colSpan];
    const colSpan = allowed.includes(fromSaved.colSpan as 4 | 6 | 8 | 12)
      ? (fromSaved.colSpan as 4 | 6 | 8 | 12)
      : def.colSpan;
    return {
      id: def.id,
      order: typeof fromSaved.order === 'number' ? fromSaved.order : def.order,
      colSpan,
      visible: fromSaved.visible !== false,
      collapsed: !!fromSaved.collapsed,
    };
  }).sort((a, b) => a.order - b.order);
}

